import { useState, useMemo } from "react";
import { cn } from "@/lib/utils";
import { getIcon } from "@/lib/iconRegistry";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ArrowLeft, ArrowRight, Check, HelpCircle, Lock, Sparkles, Copy, CheckCheck, Loader2, Camera , ImageIcon} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface WizardOption {
  key: string;
  label: string;
  description?: string;
  emoji?: string;
  image?: string;
  locked?: boolean;
}

interface WizardField {
  key: string;
  label: string;
  options: WizardOption[];
  conditionalOptions?: Record<string, WizardOption[]>;
  showWhen?: { field: string; values: string[] };
}

interface WizardStep {
  id: string;
  title: string;
  subtitle: string;
  icon: string;
  type: string;
  fieldKey?: string;
  fields?: WizardField[];
  options?: WizardOption[];
  placeholder?: string;
  showWhen?: { field: string; values: string[] };
}

interface ReferenceImage {
  tag: string;
  label: string;
  description: string;
}

interface ProductConfigType {
  version?: string;
  productType?: string;
  brand?: string;
  model?: string;
  productDescription?: string;
  dimensions?: {
    width_cm?: number;
    height_cm?: number;
    depth_cm?: number;
    note?: string;
  };
  appearance?: {
    primaryColor?: string;
    secondaryColor?: string;
    finish?: string;
    texture?: string;
    shape?: string;
    distinguishingFeatures?: string[];
  };
  placement?: {
    mountingType?: string;
    idealHeight?: string;
    position?: string;
    avoidPositions?: string[];
  };
  referenceImages?: ReferenceImage[];
}

interface WizardConfigType {
  version: string;
  steps: WizardStep[];
  constraints?: Record<string, any>;
  promptTemplate?: string;
  promptFragments?: Record<string, Record<string, string>>;
}

function OptionCard({ selected, label, description, emoji, locked, onClick }: {
  selected: boolean;
  label: string;
  description?: string;
  emoji?: string;
  locked?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={() => !locked && onClick()}
      disabled={locked}
      className={cn(
        "flex items-start gap-3 w-full rounded-xl text-left transition-all border p-4",
        locked
          ? "border-border/50 bg-card/40 opacity-55 cursor-not-allowed"
          : selected
          ? "border-primary bg-primary/5 ring-1 ring-primary/30"
          : "border-border bg-card hover:border-primary/30 hover:bg-secondary/50"
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {emoji && <span className="text-xl shrink-0">{emoji}</span>}
          <span className="text-sm font-medium">{label}</span>
          {selected && !locked && <Check className="w-3.5 h-3.5 text-primary shrink-0" />}
          {locked && <Lock className="w-3 h-3 text-muted-foreground shrink-0" />}
        </div>
        {description && (
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{description}</p>
        )}
        {locked && (
          <span className="text-[10px] text-muted-foreground/60 mt-1 block">Contact admin to unlock</span>
        )}
      </div>
    </button>
  );
}

/** Assemble prompt from template + fragments + answers + product config */
function assemblePrompt(
  template: string,
  fragments: Record<string, Record<string, string>>,
  answers: Record<string, string>,
  product?: ProductConfigType
): string {
  let result = template;

  // Replace {{_product_description}} — skip gracefully if not set
  const desc = product?.productDescription;
  result = result.replace(/\{\{_product_description\}\}/g, desc || "");

  // Replace {{_reference_image_list}} with image descriptions
  const refImgs = product?.referenceImages;
  if (refImgs && refImgs.length > 0) {
    const imageList = refImgs.map((img) => `${img.label} (${img.description})`).join(", ");
    result = result.replace(/\{\{_reference_image_list\}\}/g, imageList);
  } else {
    result = result.replace(/\{\{_reference_image_list\}\}/g, "[no reference images defined]");
  }

  // Replace {{_product_details}} with structured product info
  if (product) {
    const details: string[] = [];
    if (product.brand || product.model) {
      details.push(`Brand/Model: ${[product.brand, product.model].filter(Boolean).join(" ")}`);
    }
    if (product.dimensions) {
      const d = product.dimensions;
      details.push(`Dimensions: ${d.width_cm || "?"}cm W × ${d.height_cm || "?"}cm H × ${d.depth_cm || "?"}cm D${d.note ? ` (${d.note})` : ""}`);
    }
    if (product.appearance) {
      const a = product.appearance;
      if (a.primaryColor) details.push(`Color: ${a.primaryColor}${a.secondaryColor ? `, accent: ${a.secondaryColor}` : ""}`);
      if (a.finish) details.push(`Finish: ${a.finish}`);
      if (a.texture) details.push(`Texture: ${a.texture}`);
      if (a.shape) details.push(`Shape: ${a.shape}`);
      if (a.distinguishingFeatures?.length) {
        details.push(`Key features: ${a.distinguishingFeatures.join("; ")}`);
      }
    }
    if (product.placement) {
      const p = product.placement;
      if (p.mountingType) {
        const mountingDescriptions: Record<string, string> = {
          wall_high: "Mounted high on the wall, near the ceiling",
          wall_mid: "Mounted at mid-height on the wall",
          wall_low: "Mounted low on the wall, near the floor (NOT high up)",
          floor: "Standing on the floor",
          ceiling: "Mounted on or in the ceiling",
        };
        details.push(`Mounting: ${mountingDescriptions[p.mountingType] || p.mountingType}`);
      }
      if (p.idealHeight) details.push(`Ideal height: ${p.idealHeight}`);
      if (p.position) details.push(`Position: ${p.position}`);
      if (p.avoidPositions?.length) details.push(`Avoid: ${p.avoidPositions.join("; ")}`);
    }
    result = result.replace(/\{\{_product_details\}\}/g, details.join("\n"));
  } else {
    result = result.replace(/\{\{_product_details\}\}/g, "");
  }

  // Replace all {{field_key}} placeholders
  const placeholders = result.match(/\{\{(\w+)\}\}/g) || [];
  for (const placeholder of placeholders) {
    const fieldKey = placeholder.replace(/\{\{|\}\}/g, "");
    const answerValue = answers[fieldKey] || "";
    const fragmentMap = fragments[fieldKey];

    if (fragmentMap && answerValue && fragmentMap[answerValue]) {
      result = result.replace(placeholder, fragmentMap[answerValue]);
    } else if (answerValue) {
      result = result.replace(placeholder, answerValue);
    } else {
      result = result.replace(placeholder, "");
    }
  }

  // Clean up
  result = result
    .split("\n")
    .map((line) => line.trimEnd())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  return result;
}

interface PhotographerConfig {
  id: string;
  name: string;
  config: {
    emoji?: string;
    description?: string;
    settings?: Record<string, string>;
    [key: string]: any;
  };
}

interface WizardPreviewProps {
  config: WizardConfigType;
  productConfig?: ProductConfigType;
  photographerConfigs?: PhotographerConfig[];
  mode?: "preview" | "generate";
  initialAnswers?: Record<string, string>;
  onSave?: (answers: Record<string, string>) => void;
  onAnswerChange?: (answers: Record<string, string>) => void;
  saving?: boolean;
}

export function WizardPreview({ config, productConfig, photographerConfigs, mode = "preview", initialAnswers, onSave, onAnswerChange, saving }: WizardPreviewProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>(initialAnswers || {});
  const [showPrompt, setShowPrompt] = useState(false);
  const [copied, setCopied] = useState(false);

  const hasCameraStep = !!(photographerConfigs && photographerConfigs.length > 0);

  // Filter steps by showWhen conditions
  const isStepVisible = (s: WizardStep) => {
    if (!s.showWhen) return true;
    const depValue = answers[s.showWhen.field] || "";
    return s.showWhen.values.includes(depValue);
  };

  const allSteps = config.steps;
  const visibleSteps = allSteps.filter(isStepVisible);
  const totalSteps = visibleSteps.length + (hasCameraStep ? 1 : 0);
  const isCameraStep = hasCameraStep && currentStep === visibleSteps.length;

  // Clamp currentStep if visible steps changed
  const clampedStep = Math.min(currentStep, totalSteps - 1);
  const step = isCameraStep ? null : visibleSteps[clampedStep];

  const hasPromptConfig = !!(config.promptTemplate && config.promptFragments);

  const assembledPrompt = useMemo(() => {
    if (!config.promptTemplate || !config.promptFragments) return "";
    return assemblePrompt(config.promptTemplate, config.promptFragments, answers, productConfig);
  }, [config.promptTemplate, config.promptFragments, productConfig, answers]);

  if (!step && !isCameraStep) return null;

  const StepIcon = step ? getIcon(step.icon) : null;

  const setAnswer = (key: string, value: string) => {
    setAnswers((prev) => {
      const next = { ...prev, [key]: value };
      onAnswerChange?.(next);
      return next;
    });
  };

  const getAnswer = (key: string) => answers[key] || "";

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(assembledPrompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy to clipboard:", err);
    }
  };

  const renderTileSelect = (s: WizardStep) => {
    const fieldKey = s.fieldKey!;
    const options = s.options || [];
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {options.map((opt) => (
          <OptionCard key={opt.key} selected={getAnswer(fieldKey) === opt.key} label={opt.label} description={opt.description} emoji={opt.emoji} onClick={() => setAnswer(fieldKey, opt.key)} />
        ))}
      </div>
    );
  };

  const renderRegionPicker = (s: WizardStep) => {
    const fieldKey = s.fieldKey!;
    const options = s.options || [];
    const unlocked = options.filter((o) => !o.locked);
    const locked = options.filter((o) => o.locked);
    return (
      <div className="space-y-6">
        {unlocked.length > 0 && (
          <div>
            <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">Your Regions</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {unlocked.map((opt) => (
                <OptionCard key={opt.key} selected={getAnswer(fieldKey) === opt.key} label={opt.label} description={opt.description} emoji={opt.emoji} onClick={() => setAnswer(fieldKey, opt.key)} />
              ))}
            </div>
          </div>
        )}
        {locked.length > 0 && (
          <div>
            <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">Available to Unlock</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {locked.map((opt) => (
                <OptionCard key={opt.key} selected={false} label={opt.label} description={opt.description} emoji={opt.emoji} locked onClick={() => {}} />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderGroupedSelect = (s: WizardStep) => {
    const fields = s.fields || [];
    return (
      <div className="space-y-6">
        {fields.map((field) => {
          if (field.showWhen) {
            const depValue = getAnswer(field.showWhen.field);
            if (!field.showWhen.values.includes(depValue)) return null;
          }
          let options = [...field.options];
          if (field.conditionalOptions) {
            const zoneVal = getAnswer("zone_key");
            if (zoneVal === "utility" && field.conditionalOptions["zone_utility"]) {
              options = field.conditionalOptions["zone_utility"];
            }
            const regionVal = getAnswer("market_key");
            const regionKey = `region_${regionVal}`;
            if (field.conditionalOptions[regionKey]) {
              options = [...options, ...field.conditionalOptions[regionKey]];
            }
          }
          return (
            <div key={field.key}>
              <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">{field.label}</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {options.map((opt) => (
                  <OptionCard key={opt.key} selected={getAnswer(field.key) === opt.key} label={opt.label} description={opt.description} emoji={opt.emoji} onClick={() => setAnswer(field.key, opt.key)} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderDualSelect = (s: WizardStep) => {
    const fields = s.fields || [];
    return (
      <div className="space-y-6">
        {fields.map((field) => (
          <div key={field.key}>
            <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">{field.label}</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {field.options.map((opt) => (
                <OptionCard key={opt.key} selected={getAnswer(field.key) === opt.key} label={opt.label} description={opt.description} emoji={opt.emoji} onClick={() => setAnswer(field.key, opt.key)} />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderTextarea = (s: WizardStep) => (
    <Textarea
      value={getAnswer(s.fieldKey || "prompt")}
      onChange={(e) => setAnswer(s.fieldKey || "prompt", e.target.value)}
      placeholder={s.placeholder || "Enter additional notes..."}
      className="min-h-[160px] text-sm"
    />
  );

  const renderReferenceImage = (s: WizardStep) => (
    <div className="space-y-3">
      <div className="w-full rounded-lg border-2 border-dashed border-border/60 py-10 flex flex-col items-center justify-center gap-2 text-muted-foreground">
        <ImageIcon className="w-6 h-6" />
        <span className="text-sm font-medium">Upload reference image</span>
        <span className="text-xs">
          {s.placeholder || "PNG or JPG — used as visual direction for the generation"}
        </span>
      </div>
      {(s as any).referenceInstruction && (
        <div className="rounded-lg bg-muted/50 border border-border p-3">
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">
            Instruction sent to the model
          </p>
          <p className="text-xs text-muted-foreground">{(s as any).referenceInstruction}</p>
        </div>
      )}
      <p className="text-[11px] text-muted-foreground">
        Preview only — uploads are disabled here. In Studio the user uploads an
        image and it is attached to the generation.
      </p>
    </div>
  );

  const renderStepContent = () => {
    if (!step) return null;
    switch (step.type) {
      case "region_picker": return renderRegionPicker(step);
      case "tile_select": return renderTileSelect(step);
      case "grouped_select": return renderGroupedSelect(step);
      case "dual_select": return renderDualSelect(step);
      case "textarea": return renderTextarea(step);
      case "reference_image": return renderReferenceImage(step);
      default: return <p className="text-muted-foreground text-sm">Unknown step type: {step.type}</p>;
    }
  };

  const renderCameraStep = () => {
    if (!photographerConfigs) return null;
    const selectedId = answers["_photographer_config_id"] || "";
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {photographerConfigs.map((pc) => {
          const cfg = pc.config as any;
          const selected = selectedId === pc.id;
          return (
            <button
              key={pc.id}
              onClick={() => setAnswer("_photographer_config_id", pc.id)}
              className={cn(
                "flex items-start gap-3 w-full rounded-xl text-left transition-all border p-4",
                selected
                  ? "border-primary bg-primary/5 ring-1 ring-primary/30"
                  : "border-border bg-card hover:border-primary/30 hover:bg-secondary/50"
              )}
            >
              <span className="text-xl shrink-0">{cfg.emoji || "📷"}</span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{pc.name}</span>
                  {selected && <Check className="w-3.5 h-3.5 text-primary shrink-0" />}
                </div>
                {cfg.description && (
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{cfg.description}</p>
                )}
              </div>
            </button>
          );
        })}
      </div>
    );
  };

  const isLastStep = currentStep === totalSteps - 1;

  return (
    <div className="flex flex-col h-full">
      {/* Step indicator */}
      <div className="flex items-center gap-1 px-4 py-3 border-b border-border bg-card shrink-0">
        {visibleSteps.map((s, i) => {
          const Icon = getIcon(s.icon);
          return (
            <button
              key={s.id}
              onClick={() => { setCurrentStep(i); setShowPrompt(false); }}
              className={cn(
                "flex items-center gap-1.5 px-2 py-1 rounded-md text-xs transition-colors",
                i === currentStep && !showPrompt && !isCameraStep
                  ? "bg-primary/10 text-primary font-medium"
                  : i < currentStep
                  ? "text-primary/60"
                  : "text-muted-foreground"
              )}
            >
              {Icon ? <Icon className="w-3 h-3" /> : <HelpCircle className="w-3 h-3" />}
              <span className="hidden lg:inline">{s.title}</span>
            </button>
          );
        })}
        {hasCameraStep && (
          <button
            onClick={() => { setCurrentStep(visibleSteps.length); setShowPrompt(false); }}
            className={cn(
              "flex items-center gap-1.5 px-2 py-1 rounded-md text-xs transition-colors",
              isCameraStep && !showPrompt
                ? "bg-primary/10 text-primary font-medium"
                : currentStep > visibleSteps.length
                ? "text-primary/60"
                : "text-muted-foreground"
            )}
          >
            <Camera className="w-3 h-3" />
            <span className="hidden lg:inline">Camera Style</span>
          </button>
        )}
        {hasPromptConfig && (
          <button
            onClick={() => setShowPrompt(true)}
            className={cn(
              "flex items-center gap-1.5 px-2 py-1 rounded-md text-xs transition-colors ml-auto",
              showPrompt ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground hover:text-foreground"
            )}
          >
            <Sparkles className="w-3 h-3" />
            <span className="hidden lg:inline">Prompt Result</span>
          </button>
        )}
      </div>

      {/* Content area */}
      {showPrompt ? (
        <div className="flex-1 overflow-y-auto p-4">
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4 text-primary" />
              <h3 className="text-base font-semibold">Assembled Prompt</h3>
            </div>
            <p className="text-sm text-muted-foreground">
              This is the final prompt that would be sent to the AI, assembled from your wizard selections.
            </p>
          </div>
          <div className="relative">
            <pre className="w-full rounded-lg border border-border bg-muted/50 p-4 font-mono text-xs leading-relaxed text-foreground whitespace-pre-wrap overflow-auto min-h-[200px]">
              {assembledPrompt || "Complete wizard steps to see the assembled prompt."}
            </pre>
            {assembledPrompt && (
              <Button size="sm" variant="ghost" onClick={handleCopy} className="absolute top-2 right-2">
                {copied ? <CheckCheck className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              </Button>
            )}
          </div>
          {config.promptFragments && (
            <div className="mt-6">
              <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-3 block">Field Status</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {Object.keys(config.promptFragments).map((fieldKey) => {
                  const value = answers[fieldKey];
                  const hasValue = !!value;
                  return (
                    <div key={fieldKey} className={cn("flex items-center gap-2 px-3 py-2 rounded-lg border text-xs", hasValue ? "border-primary/30 bg-primary/5 text-foreground" : "border-border bg-card text-muted-foreground")}>
                      {hasValue ? <Check className="w-3 h-3 text-primary shrink-0" /> : <div className="w-3 h-3 rounded-full border border-muted-foreground/40 shrink-0" />}
                      <span className="font-mono truncate">{fieldKey}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      ) : isCameraStep ? (
        <div className="flex-1 overflow-y-auto p-4">
          <AnimatePresence mode="wait">
            <motion.div key="camera-step" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-1">
                  <Camera className="w-4 h-4 text-primary" />
                  <h3 className="text-base font-semibold">Camera Style</h3>
                </div>
                <p className="text-sm text-muted-foreground">Choose the photographic style for this visual</p>
              </div>
              {renderCameraStep()}
            </motion.div>
          </AnimatePresence>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto p-4">
          <AnimatePresence mode="wait">
            <motion.div key={step!.id} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-1">
                  {StepIcon && <StepIcon className="w-4 h-4 text-primary" />}
                  <h3 className="text-base font-semibold">{step!.title}</h3>
                </div>
                <p className="text-sm text-muted-foreground">{step!.subtitle}</p>
              </div>
              {renderStepContent()}
            </motion.div>
          </AnimatePresence>
        </div>
      )}

      {/* Navigation footer */}
      <div className="shrink-0 border-t border-border bg-card/80 backdrop-blur-sm px-4 py-3 flex items-center justify-between">
        <Button
          variant="outline"
          size="sm"
          disabled={currentStep === 0 && !showPrompt}
          onClick={() => {
            if (showPrompt) {
              setShowPrompt(false);
              setCurrentStep(totalSteps - 1);
            } else {
              setCurrentStep((s) => Math.max(0, s - 1));
            }
          }}
        >
          <ArrowLeft className="w-3.5 h-3.5 mr-1" />
          Back
        </Button>
        <span className="text-xs text-muted-foreground">
          {showPrompt ? "Prompt Result" : `${currentStep + 1} / ${totalSteps}`}
        </span>
        {mode === "generate" && isLastStep && !showPrompt ? (
          <Button size="sm" className="gradient-primary text-primary-foreground" onClick={() => onSave?.(answers)} disabled={saving}>
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}
            Save Brief
          </Button>
        ) : isLastStep && hasPromptConfig && !showPrompt ? (
          <Button size="sm" onClick={() => setShowPrompt(true)}>
            <Sparkles className="w-3.5 h-3.5 mr-1" />
            View Prompt
          </Button>
        ) : (
          <Button size="sm" disabled={isLastStep || showPrompt} onClick={() => setCurrentStep((s) => Math.min(totalSteps - 1, s + 1))}>
            Next
            <ArrowRight className="w-3.5 h-3.5 ml-1" />
          </Button>
        )}
      </div>
    </div>
  );
}
